import { Button } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { addChangeListener } from 'stores/DynamicProperties/changeListeners';
import type DynamicGroup from 'stores/DynamicProperties/Types/Group';
import { useForceUpdate } from 'utils/Hooks';
import { camelCaseToTitle } from 'utils/String';

import PropertyPopover from '../PropertyPopover';
import InputContainer, {
    type InputContainerSize,
    type InputHint,
} from './InputContainer';

const Container = styled.div`
    display: flex;
    width: 100%;
    margin: 18px 0 10px;
    padding: 10px 10px 3px;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
    position: relative;
`;

const GroupContainer = styled.div`
    padding-top: 5px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 17px;
`;

const GroupLabel = styled.div`
    background: white;
    display: flex;
    position: absolute;
    font-size: 13px;
    font-weight: bold;
    top: -17px;
    left: 8px;
    align-items: center;
    justify-content: center;
    padding: 5px 6px;
`;

const ExpandButton = styled(Button)`
    width: calc(100% + 2px);
    display: flex;
    height: 20px;
    position: absolute;
    bottom: -1px;
    left: -1px;
    background: #f0f0f0;
    justify-content: center;
    line-height: 16px;
    border-radius: 0 0 5px 5px;
    font-size: 12px;

    &:active,
    &:hover,
    &:visited,
    &:focus {
        background: #f0f0f0;
        border-color: #d9d9d9;
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
`;

const PropertiesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
    height: 278px;
    transition: all 0.5s;
    overflow: hidden;

    &.collapsed {
        height: 0;
    }
`;

interface PropertySpec {
    name: string;
    size: InputContainerSize;
    inputHint?: InputHint;
}

const majorProperties: ReadonlyArray<PropertySpec> = [
    { name: 'font', size: 'full' },
    { name: 'text', size: 'full', inputHint: { variant: 'multiline' } },
    { name: 'color', size: 'third' },
    { name: 'fontSize', size: 'third' },
    {
        name: 'textAlign',
        size: 'third',
        inputHint: { variant: 'enum', values: ['left', 'center', 'right'] },
    },
];

const minorProperties: ReadonlyArray<PropertySpec> = [
    {
        name: 'verticalAlign',
        size: 'third',
        inputHint: { variant: 'enum', values: ['top', 'middle', 'bottom'] },
    },
    { name: 'letterSpacing', size: 'third' },
    { name: 'lineHeight', size: 'third' },
    { name: 'top', size: 'quarter' },
    { name: 'left', size: 'quarter' },
    { name: 'width', size: 'quarter' },
    { name: 'height', size: 'quarter' },
    { name: 'offsetX', size: 'half' },
    { name: 'offsetY', size: 'half' },
    { name: 'dropshadowColor', size: 'third' },
    { name: 'dropshadowBlurX', size: 'third' },
    { name: 'dropshadowBlurY', size: 'third' },
    { name: 'dropshadowOffsetX', size: 'third' },
    { name: 'dropshadowOffsetY', size: 'third' },
    { name: 'dropshadowOpacity', size: 'third' },
    { name: 'strokeColor', size: 'third' },
    {
        name: 'strokeLinecap',
        size: 'third',
        inputHint: { variant: 'enum', values: ['butt', 'round', 'square'] },
    },
    { name: 'strokeWidth', size: 'third' },
    { name: 'strokeOpacity', size: 'third' },
    { name: 'maskRows', size: 'third' },
    { name: 'boundingBox', size: 'third' },
];

function renderPropertySpec(
    property: DynamicGroup,
    disabled: boolean,
    { name, ...props }: PropertySpec
) {
    const childProperty = property.getChild(name);
    if (!childProperty) return null;
    return (
        <InputContainer
            key={name}
            property={childProperty}
            disabled={disabled}
            {...props}
        />
    );
}

export interface SVGTextInputGroupProps {
    disabled?: boolean;
    editMode?: boolean;
    property: DynamicGroup;
}

function SVGTextInputGroup({
    disabled = false,
    editMode = false,
    property,
}: SVGTextInputGroupProps) {
    const forceUpdate = useForceUpdate();
    useEffect(
        () => addChangeListener(property, forceUpdate),
        [property, forceUpdate]
    );

    const [collapsed, setCollapsed] = useState(true);

    return (
        <Container>
            {property.name && (
                <PropertyPopover property={property} editMode={editMode}>
                    <GroupLabel>{camelCaseToTitle(property.name)}</GroupLabel>
                </PropertyPopover>
            )}

            <GroupContainer>
                {/* Above the fold */}
                <TextContainer>
                    {majorProperties.map((ps) =>
                        renderPropertySpec(property, disabled, ps)
                    )}
                </TextContainer>

                {/* Click-to-expand container */}
                <PropertiesContainer className={collapsed ? 'collapsed' : ''}>
                    {minorProperties.map((ps) =>
                        renderPropertySpec(property, disabled, ps)
                    )}
                </PropertiesContainer>
            </GroupContainer>

            <ExpandButton onClick={() => setCollapsed((c) => !c)}>
                {collapsed ? '+ More' : '- Less'}
            </ExpandButton>
        </Container>
    );
}

export default observer(SVGTextInputGroup);
